'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import classes from './NeumoHeader.module.scss';
import Cookies from 'js-cookie';
// ✅ Mantine
import { Group, Menu, UnstyledButton } from '@mantine/core';
import { gaClick, gaCta, gaLanguageSwitch } from '@/src/helpers/gtag';

type HeaderLink = { label: string; href: string; description?: string };

type HeaderColumn = {
  title: string;
  links: HeaderLink[];
};

type HeaderNavItem = {
  label: string;
  href?: string;
  columns?: HeaderColumn[];
};

export type NeumoHeaderSlice = {
  type: 'neumo-header';
  logoSrc: string;
  logoAlt: string;
  logoText?: string;
  nav: HeaderNavItem[];
  cta?: { label: string; href: string };

  auth: {
    dashboardLabel: string;
    dashboardHref: string;
    loginLabel: string;
    loginHref: string;
    logoutLabel: string;
    afterLogoutHref?: string;
  };

  supportedLanguage: {
    fr: { label: string; img: string };
    en: { label: string; img: string };
  };
  selectedLanguage?: 'fr' | 'en';
};

// --- helpers ---
const LOCALES = ['fr', 'en'] as const;
type Locale = (typeof LOCALES)[number];

function getLocaleFromPath(pathname: string): Locale | null {
  const first = pathname.split('/')[1];
  return (LOCALES as readonly string[]).includes(first) ? (first as Locale) : null;
}

function withLocalePrefix(pathname: string, nextLocale: Locale): string {
  const parts = pathname.split('/');
  const first = parts[1];

  if ((LOCALES as readonly string[]).includes(first)) {
    parts[1] = nextLocale;
    return parts.join('/') || `/${nextLocale}`;
  }

  return `/${nextLocale}${pathname.startsWith('/') ? '' : '/'}${pathname}`;
}

export function NeumoHeader({
  data,
  hideOnScroll = false,
}: {
  data: NeumoHeaderSlice;
  hideOnScroll?: boolean;
}) {
  const pathname = usePathname();
  const router = useRouter();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastScrollY = useRef(0);

  // Re-check cookie on navigation
  useEffect(() => {
    setIsLoggedIn(!!Cookies.get('authToken'));
  }, [pathname]);

  // Scroll hide logic — smooth with direction detection
  useEffect(() => {
    if (!hideOnScroll) {
      return;
    }

    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY > lastScrollY.current && currentY > 120) {
        setHidden(true);
      } else if (currentY < lastScrollY.current) {
        setHidden(false);
      }
      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hideOnScroll]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleLogout = () => {
    Cookies.remove('authToken');
    window.location.href = data.auth.afterLogoutHref ?? '/';
  };

  const currentLocale: Locale = useMemo(() => {
    const cookieLocale = Cookies.get('NEXT_LOCALE') as Locale | undefined;
    if (cookieLocale === 'fr' || cookieLocale === 'en') {
      return cookieLocale;
    }

    const pathLocale = getLocaleFromPath(pathname);
    if (pathLocale) {
      return pathLocale;
    }

    if (data.selectedLanguage === 'fr' || data.selectedLanguage === 'en') {
      return data.selectedLanguage;
    }

    return 'fr';
  }, [pathname, data.selectedLanguage]);

  const changeLanguage = (nextLocale: Locale) => {
    if (nextLocale === currentLocale) {
      return;
    }

    Cookies.set('NEXT_LOCALE', nextLocale, { expires: 365, path: '/' });
    const nextPath = withLocalePrefix(pathname, nextLocale);
    router.push(nextPath);
    router.refresh();
  };

  const currentLangMeta = data.supportedLanguage[currentLocale];

  return (
    <>
      <header className={`${classes.header} ${hidden ? classes.headerHidden : ''}`} role="banner">
        <div className={classes.inner}>
          <Link
            href="/"
            className={classes.logo}
            aria-label={data.logoText || 'Averiane'}
            onClick={() => gaClick('header_logo', { href: '/' })}
          >
            <Image
              src={data.logoSrc}
              alt={data.logoAlt}
              width={32}
              height={32}
              priority
              className={classes.logoImg}
            />
            <span className={classes.logoText}>{data.logoText || 'Averiane'}</span>
          </Link>

          <nav className={classes.nav} aria-label="Primary">
            <ul className={classes.navList}>
              {data.nav.map((item) => {
                const hasDropdown = item.columns && item.columns.length > 0;

                if (!hasDropdown) {
                  return (
                    <li key={item.label} className={classes.navItem}>
                      <Link
                        className={classes.navLink}
                        href={item.href ?? '#'}
                        onClick={() =>
                          gaClick('header_nav_link', { label: item.label, href: item.href ?? '#' })
                        }
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                }

                return (
                  <li key={item.label} className={`${classes.navItem} ${classes.hasDropdown}`}>
                    <button type="button" className={classes.navButton} aria-haspopup="true">
                      {item.label}
                      <span className={classes.chevron} aria-hidden="true">
                        ▾
                      </span>
                    </button>

                    <div className={classes.mega} role="menu">
                      <div className={classes.megaInner}>
                        {item.columns!.map((col) => (
                          <div key={col.title || col.links[0]?.href} className={classes.col}>
                            {col.title && <p className={classes.colTitle}>{col.title}</p>}
                            <ul className={classes.colList}>
                              {col.links.map((l) => (
                                <li key={l.href} className={classes.colItem}>
                                  <Link
                                    href={l.href}
                                    className={classes.megaLink}
                                    onClick={() =>
                                      gaClick('header_mega_link', { label: l.label, href: l.href })
                                    }
                                  >
                                    <span className={classes.megaLabel}>{l.label}</span>
                                    {l.description && (
                                      <span className={classes.megaDesc}>{l.description}</span>
                                    )}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className={classes.actions}>
            <div className={classes.rightLinks}>
              <Menu withinPortal position="bottom-end" offset={8} shadow="md">
                <Menu.Target>
                  <UnstyledButton className={classes.langButton} aria-label="Change language">
                    <Group gap={8} wrap="nowrap">
                      <Image
                        src={currentLangMeta.img}
                        alt={currentLangMeta.label}
                        width={20}
                        height={20}
                        style={{ borderRadius: 999 }}
                      />
                      <span className={classes.langChevron} aria-hidden="true">
                        ▾
                      </span>
                    </Group>
                  </UnstyledButton>
                </Menu.Target>

                <Menu.Dropdown>
                  {(['fr', 'en'] as Locale[]).map((loc) => {
                    const meta = data.supportedLanguage[loc];
                    return (
                      <Menu.Item
                        key={loc}
                        onClick={() => {
                          gaLanguageSwitch(loc);
                          changeLanguage(loc);
                        }}
                        leftSection={
                          <img
                            src={meta.img}
                            alt={meta.label}
                            width={18}
                            height={18}
                            style={{ borderRadius: 999 }}
                          />
                        }
                      >
                        {meta.label}
                      </Menu.Item>
                    );
                  })}
                </Menu.Dropdown>
              </Menu>

              {isLoggedIn ? (
                <>
                  <button type="button" onClick={handleLogout} className={classes.rightLinkButton}>
                    {data.auth.logoutLabel}
                  </button>
                  <Link href={data.auth.dashboardHref} className={classes.rightLink}>
                    {data.auth.dashboardLabel}
                  </Link>
                </>
              ) : (
                <Link href={data.auth.loginHref} className={classes.rightLink}>
                  {data.auth.loginLabel}
                </Link>
              )}
            </div>

            {data.cta && (
              <Link
                href={data.cta.href}
                className={classes.cta}
                onClick={() => gaCta(data.cta!.label, 'header_cta', data.cta!.href)}
              >
                {data.cta.label}
              </Link>
            )}

            <button
              type="button"
              className={classes.burger}
              onClick={() => {
                gaClick('header_mobile_menu_open');
                setMobileOpen(true);
              }}
              aria-label="Ouvrir le menu"
              aria-expanded={mobileOpen}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`${classes.mobile} ${mobileOpen ? classes.mobileOpen : ''}`}
        aria-hidden={!mobileOpen}
      >
        <div className={classes.mobileInner}>
          <div className={classes.mobileTop}>
            <Link
              href="/"
              className={classes.mobileBrand}
              onClick={() => {
                gaClick('header_mobile_logo', { href: '/' });
                setMobileOpen(false);
              }}
              aria-label={data.logoText || 'Averiane'}
            >
              <Image
                src={data.logoSrc}
                alt={data.logoAlt}
                width={32}
                height={32}
                className={classes.logoImg}
              />
              <span className={classes.logoText}>{data.logoText || 'Averiane'}</span>
            </Link>
            <button
              type="button"
              className={classes.mobileClose}
              onClick={() => {
                gaClick('header_mobile_menu_close');
                setMobileOpen(false);
              }}
              aria-label="Fermer le menu"
            >
              ×
            </button>
          </div>

          <div className={classes.mobileContent}>
            {data.nav.map((item) => {
              if (!item.columns || item.columns.length === 0) {
                return (
                  <Link
                    key={item.label}
                    href={item.href ?? '#'}
                    className={classes.mobileLink}
                    onClick={() => {
                      gaClick('header_mobile_nav_link', {
                        label: item.label,
                        href: item.href ?? '#',
                      });
                      setMobileOpen(false);
                    }}
                  >
                    {item.label}
                  </Link>
                );
              }

              return (
                <details key={item.label} className={classes.mobileSection}>
                  <summary className={classes.mobileSummary}>{item.label}</summary>
                  <div className={classes.mobileColumns}>
                    {item.columns.map((col) => (
                      <div key={col.title || col.links[0]?.href}>
                        {col.title && <p className={classes.mobileColTitle}>{col.title}</p>}
                        <ul className={classes.mobileColList}>
                          {col.links.map((l) => (
                            <li key={l.href}>
                              <Link
                                href={l.href}
                                className={classes.mobileSublink}
                                onClick={() => {
                                  gaClick('header_mobile_sublink', {
                                    label: l.label,
                                    href: l.href,
                                  });
                                  setMobileOpen(false);
                                }}
                              >
                                {l.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </details>
              );
            })}
          </div>

          <div className={classes.mobileActions}>
            {data.cta && (
              <Link
                href={data.cta.href}
                className={classes.mobileCta}
                onClick={() => {
                  gaCta(data.cta!.label, 'header_mobile_cta', data.cta!.href);
                  setMobileOpen(false);
                }}
              >
                {data.cta.label}
              </Link>
            )}

            <div className={classes.mobileLang}>
              {(['fr', 'en'] as Locale[]).map((loc) => (
                <button
                  key={loc}
                  type="button"
                  className={`${classes.mobileLangButton} ${
                    loc === currentLocale ? classes.mobileLangActive : ''
                  }`}
                  onClick={() => {
                    gaLanguageSwitch(loc);
                    changeLanguage(loc);
                    setMobileOpen(false);
                  }}
                >
                  {loc.toUpperCase()}
                </button>
              ))}
            </div>

            <div className={classes.mobileAuth}>
              {isLoggedIn ? (
                <>
                  <Link
                    href={data.auth.dashboardHref}
                    className={classes.mobileAuthLink}
                    onClick={() => {
                      gaClick('header_mobile_dashboard', { href: data.auth.dashboardHref });
                      setMobileOpen(false);
                    }}
                  >
                    {data.auth.dashboardLabel}
                  </Link>
                  <button
                    type="button"
                    className={classes.mobileAuthButton}
                    onClick={() => {
                      handleLogout();
                      setMobileOpen(false);
                    }}
                  >
                    {data.auth.logoutLabel}
                  </button>
                </>
              ) : (
                <Link
                  href={data.auth.loginHref}
                  className={classes.mobileAuthLink}
                  onClick={() => {
                    gaClick('header_mobile_login', { href: data.auth.loginHref });
                    setMobileOpen(false);
                  }}
                >
                  {data.auth.loginLabel}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
